import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useCurrency } from "../context/CurrencyContext";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const PLANS = [
  { id: "basique", name: "Basique", price: 29, features: ["10 annonces", "1 agent", "Support email"] },
  { id: "standard", name: "Standard", price: 79, features: ["50 annonces", "5 agents", "Support prioritaire", "Mise en avant"] },
  { id: "premium", name: "Premium", price: 199, features: ["Annonces illimitées", "Agents illimités", "Support 24/7", "API & branding"] },
];

export default function BecomeAgency() {
  const { lang } = useI18n();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "",
    city: "", country: "Madagascar", website: "", plan: "standard", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/agency-applications", form);
      setSuccess(true);
      toast.success(lang === "fr" ? "Demande envoyée." : "Application submitted.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-[70vh] flex items-center justify-center py-24 px-6" data-testid="application-success">
        <div className="max-w-lg text-center">
          <div className="w-16 h-16 rounded-full bg-[#1d1d1f] text-white flex items-center justify-center mx-auto mb-6">
            <Check size={28} strokeWidth={2} />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#1d1d1f] mb-3">
            {lang === "fr" ? "Demande reçue" : "Application received"}
          </h1>
          <p className="text-[16px] text-[#86868b] mb-8">
            {lang === "fr"
              ? "Notre équipe étudie votre dossier et vous recontacte sous 48h. Vous recevrez un lien de paiement Stripe une fois votre agence validée."
              : "Our team is reviewing your application and will get back to you within 48h. A Stripe payment link will be sent once your agency is approved."}
          </p>
          <button onClick={() => navigate("/")} className="btn-pill btn-primary text-[14px]">
            {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="py-16" data-testid="become-agency-page">
      <div className="max-w-[1000px] mx-auto px-6 sm:px-8">
        <div className="text-center mb-12">
          <div className="eyebrow mb-3">{lang === "fr" ? "Devenir partenaire" : "Become a partner"}</div>
          <h1 className="headline text-4xl sm:text-5xl text-[#1d1d1f] mb-3">
            {lang === "fr" ? "Rejoignez Tafo" : "Join Tafo"}
          </h1>
          <p className="text-[17px] text-[#86868b] max-w-2xl mx-auto">
            {lang === "fr"
              ? "Publiez vos biens sur la plateforme premium de l'immobilier malgache. Validation sous 48h."
              : "List your properties on Madagascar's premium real estate platform. Approval within 48h."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-testid="plan-selector">
          {PLANS.map((p) => {
            const selected = form.plan === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setForm({ ...form, plan: p.id })}
                className={`text-left rounded-3xl p-6 border-2 transition ${
                  selected ? "border-[#1d1d1f] bg-white" : "border-transparent bg-[#f5f5f7] hover:bg-[#ececf0]"
                }`}
                data-testid={`select-plan-${p.id}`}
              >
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#86868b] mb-2">{p.name}</div>
                <div className="text-[28px] font-semibold tracking-tight text-[#1d1d1f] mb-3">
                  {format(p.price)}<span className="text-[13px] text-[#86868b] font-normal">/mois</span>
                </div>
                <ul className="space-y-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="text-[13px] text-[#1d1d1f] flex items-start gap-2">
                      <Check size={13} strokeWidth={1.8} className="mt-1 text-[#86868b]" />{f}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl border border-black/5 p-8 sm:p-10 space-y-5" data-testid="agency-application-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <F label={lang === "fr" ? "Nom de l'agence" : "Company name"} required value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="app-company" />
            <F label={lang === "fr" ? "Nom du contact" : "Contact name"} required value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="app-contact" />
            <F label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="app-email" />
            <F label={lang === "fr" ? "Téléphone" : "Phone"} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="app-phone" />
            <F label={lang === "fr" ? "Ville" : "City"} value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="app-city" />
            <F label="Pays / Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="app-country" />
            <F label="Site web" value={form.website} onChange={(v) => setForm({ ...form, website: v })} testid="app-website" />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">Message</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              placeholder={lang === "fr" ? "Parlez-nous de votre agence…" : "Tell us about your agency…"}
              className="w-full bg-[#f5f5f7] rounded-2xl px-4 py-3 text-[14px] outline-none resize-none"
              data-testid="app-message"
            />
          </div>
          <div className="flex items-center justify-between pt-4">
            <Link to="/" className="text-[13px] text-[#86868b] hover:text-[#1d1d1f]">← {lang === "fr" ? "Annuler" : "Cancel"}</Link>
            <button
              type="submit"
              disabled={submitting}
              className="btn-pill btn-primary text-[14px] flex items-center gap-2 disabled:opacity-50"
              data-testid="submit-application"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {lang === "fr" ? "Envoyer la demande" : "Submit application"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

function F({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="w-full bg-[#f5f5f7] rounded-2xl px-4 py-3 text-[14px] outline-none"
        data-testid={testid}
      />
    </div>
  );
}
